import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { COLORS, BRAND } from '@/lib/brand';
import { Truck, ClipboardCheck, LogOut, Menu, X, User, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LogisticaLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const navItems = [
    { path: '/logistica', icon: Truck, label: 'Iniciar Ruta' },
    { path: '/logistica/history', icon: ClipboardCheck, label: 'Historial' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div 
      className="min-h-screen flex flex-col" 
      style={{ backgroundColor: COLORS.background.DEFAULT }}
      data-testid="logistica-layout"
    >
      {/* Header con fondo naranja TUPU Stock */}
      <header 
        className="px-4 py-3 sticky top-0 z-50 shadow-md"
        style={{ backgroundColor: COLORS.primary.DEFAULT }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="h-12 w-12 rounded-lg flex items-center justify-center shadow-sm"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <Truck className="w-7 h-7" style={{ color: COLORS.primary.DEFAULT }} />
            </div>
            <div>
              <h1 className="font-bold text-lg text-white">Logística</h1>
              <p className="text-xs text-white/70">Despacho a locales</p>
            </div>
          </div>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: isActive(item.path) ? '#FFFFFF' : 'transparent',
                  color: isActive(item.path) ? COLORS.primary.DEFAULT : '#FFFFFF'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = COLORS.primary.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                data-testid={`logistica-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-white/80">
              <User className="w-4 h-4" />
              <span>{user?.username}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-white/90 hover:text-white hover:bg-white/10"
              data-testid="logistica-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-white"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <nav className="md:hidden mt-3 pb-2 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium"
                style={{
                  backgroundColor: isActive(item.path) ? '#FFFFFF' : 'transparent',
                  color: isActive(item.path) ? COLORS.primary.DEFAULT : '#FFFFFF'
                }}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 px-4 py-4 md:p-6 max-w-7xl mx-auto w-full" data-testid="logistica-main-content">
        {children || <Outlet />}
      </main>

      {/* Bottom Nav Mobile */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 px-4 py-2 z-40"
        style={{ backgroundColor: COLORS.background.surface, borderTop: `1px solid ${COLORS.border.DEFAULT}` }}
      >
        <div className="flex justify-around">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors"
              style={{
                color: isActive(item.path) ? COLORS.primary.DEFAULT : COLORS.text.secondary
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default LogisticaLayout;
